import { supabase } from '@/lib/supabase'
import type { Tick, TickStatus } from '@/types'

// Query key factory for consistent cache management
export const tickKeys = {
  all: ['ticks'] as const,
  byWorld: (worldId: string) => ['ticks', 'world', worldId] as const,
  current: (worldId: string) => ['ticks', 'current', worldId] as const,
  history: (worldId: string) => ['ticks', 'history', worldId] as const,
  detail: (id: string) => ['ticks', 'id', id] as const,
  status: (worldId: string, nationId?: string) =>
    nationId
      ? (['ticks', 'status', worldId, nationId] as const)
      : (['ticks', 'status', worldId] as const),
}

/**
 * Fetch the current (most recent) tick for a world
 */
export async function fetchCurrentTick(worldId: string): Promise<Tick | null> {
  const { data, error } = await supabase
    .from('ticks')
    .select('*')
    .eq('world_id', worldId)
    .order('tick_number', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) {
    throw error
  }

  return data as Tick | null
}

/**
 * Fetch tick history for a world
 */
export async function fetchTickHistory(worldId: string, limit: number = 20): Promise<Tick[]> {
  const { data, error } = await supabase
    .from('ticks')
    .select('*')
    .eq('world_id', worldId)
    .order('tick_number', { ascending: false })
    .limit(limit)

  if (error) {
    throw error
  }

  return data as Tick[]
}

/**
 * Create a new tick record with 'pending' status
 */
export async function createTick(worldId: string, tickNumber: number): Promise<Tick> {
  const { data, error } = await supabase
    .from('ticks')
    .insert({
      world_id: worldId,
      tick_number: tickNumber,
      status: 'pending' as TickStatus,
    })
    .select()
    .single()

  if (error) {
    throw error
  }

  return data as Tick
}

/**
 * Update tick status with appropriate timestamps
 * - 'processing': sets started_at to now
 * - 'completed' or 'failed': sets completed_at (uses provided date or now)
 */
export async function updateTickStatus(
  tickId: string,
  status: TickStatus,
  completedAt?: Date
): Promise<Tick> {
  const updates: { status: TickStatus; started_at?: string; completed_at?: string } = { status }

  if (status === 'processing') {
    updates.started_at = new Date().toISOString()
  }

  if (status === 'completed' || status === 'failed') {
    updates.completed_at = completedAt ? completedAt.toISOString() : new Date().toISOString()
  }

  const { data, error } = await supabase
    .from('ticks')
    .update(updates)
    .eq('id', tickId)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data as Tick
}

/**
 * Tick status data for UI display
 */
export interface TickStatusData {
  currentTick: number
  maxTick: number | null
  lastTickStatus: TickStatus
  lastTickCompletedAt: Date | null
  nextElectionTick: number | null
  ticksUntilElection: number | null
}

/**
 * Fetch comprehensive tick status for a world (and optionally a nation)
 *
 * @param worldId - The world to check
 * @param nationId - Optional nation to get election countdown
 * @returns Tick status data for UI display
 */
export async function fetchTickStatus(
  worldId: string,
  nationId?: string
): Promise<TickStatusData> {
  // Fetch world data (for current_tick)
  const { data: world, error: worldError } = await supabase
    .from('worlds')
    .select('current_tick, settings')
    .eq('id', worldId)
    .single()

  if (worldError) {
    throw worldError
  }

  // Fetch latest tick record
  const { data: latestTick, error: tickError } = await supabase
    .from('ticks')
    .select('*')
    .eq('world_id', worldId)
    .order('tick_number', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (tickError) {
    throw tickError
  }

  // Get max tick from world settings if available
  const maxTick = world.settings?.max_ticks as number | null ?? null

  let nextElectionTick: number | null = null
  let ticksUntilElection: number | null = null

  // Fetch nation data if nationId provided
  if (nationId) {
    const { data: nation, error: nationError } = await supabase
      .from('nations')
      .select('next_election_tick')
      .eq('id', nationId)
      .single()

    if (!nationError && nation?.next_election_tick) {
      nextElectionTick = nation.next_election_tick
      ticksUntilElection = nation.next_election_tick - world.current_tick
    }
  }

  return {
    currentTick: world.current_tick,
    maxTick,
    lastTickStatus: latestTick?.status ?? 'pending',
    lastTickCompletedAt: latestTick?.completed_at ? new Date(latestTick.completed_at) : null,
    nextElectionTick,
    ticksUntilElection,
  }
}
